/**
 * An idle timer for a long-running request: it fires once when nothing has
 * happened for `ms`, and every sign of life (`reset`) starts the wait again.
 *
 * The agent builder uses it per turn and per create, so a stream or request
 * that stops answering ends in an error the user can act on instead of a
 * popup that cannot be closed.
 */

/** How long the builder may stay silent before the popup gives up. */
export const BUILDER_IDLE_TIMEOUT_MS = 60_000;

export interface IdleTimeout {
  /** Restart the wait. Does nothing once the timer has fired or was cleared. */
  reset: () => void;
  /** Stop the timer for good (the work finished). */
  clear: () => void;
  /** True once the timer fired. */
  readonly fired: boolean;
}

export function createIdleTimeout(
  ms: number,
  onTimeout: () => void
): IdleTimeout {
  let handle: ReturnType<typeof setTimeout> | null = null;
  let fired = false;
  let cleared = false;

  const stop = () => {
    if (handle !== null) clearTimeout(handle);
    handle = null;
  };

  const arm = () => {
    if (fired || cleared) return;
    stop();
    handle = setTimeout(() => {
      handle = null;
      fired = true;
      onTimeout();
    }, ms);
  };

  arm();

  return {
    reset: arm,
    clear: () => {
      cleared = true;
      stop();
    },
    get fired() {
      return fired;
    },
  };
}
